import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import usePageParams from '@shared/hooks/src/usePageParams'

import { CBOStatus } from '@pages/CBOs/CBO/CBO.utils'
import Pagination from '@components/Pagination'
import CBO from '@components/TableCell/CBO'
import Provider from '@components/TableCell/Provider'
import TableEmpty from '@components/TableEmpty'
import TableLoader from '@components/TableLoader'
import TableRowLink from '@components/TableRowLink'

import { useProviderCBOs } from './ProviderCBOsDetailsModal.hooks'

export default function ProviderCBOsDetailsModal({ provider, open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      sx={{ '& .MuiDialog-container': { justifyContent: 'flex-end' } }}
    >
      <Content provider={provider} onClose={onClose} />
    </Dialog>
  )
}

function Content({ provider, onClose }) {
  const [page, perPage] = usePageParams()
  const { data, isPending } = useProviderCBOs(provider.id, {
    limit: perPage,
    offset: (page - 1) * perPage,
  })

  const showLoading = isPending
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <>
      <DialogTitle onClose={onClose}>
        <Provider user={provider} />
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>CBO</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="center">Indicators</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showLoading && <TableLoader columns={5} items={10} typography={{ variant: 'body2' }} />}
              {showEmpty && <TableEmpty columns={5} title="No CBOs found" />}
              {showData && data?.map((cbo) => <CBORow key={cbo.id} data={cbo} />)}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Pagination loading={isPending} last={data?.length < perPage} />
      </DialogActions>
    </>
  )
}

function CBORow({ data }) {
  return (
    <TableRowLink hover newTab href={`/app/cbos/${data.id}`}>
      <TableCell>
        <Typography variant="body2">{data.id}</Typography>
      </TableCell>
      <TableCell>
        <CBO small data={data} />
      </TableCell>
      <TableCell>
        <Stack spacing={-0.5}>
          <Typography variant="body2">
            <Typography variant="body2" component="span" sx={{ color: 'text.secondary', mr: 1 }}>
              Vendors:
            </Typography>
            {(data.labVendors || []).map((v) => v.name).join(', ') || '—'}
          </Typography>
          <Typography variant="body2">
            <Typography variant="body2" component="span" sx={{ color: 'text.secondary', mr: 1 }}>
              Services:
            </Typography>
            {(data.providerTypes || []).map((v) => v.name).join(', ') || '—'}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack sx={{ gap: 0.5 }}>
          <Chip
            size="small"
            label="New Patients"
            variant={data.acceptingNewPatients ? 'filled' : 'outlined'}
            color={data.acceptingNewPatients ? 'success' : 'secondary'}
          />
          <Chip
            size="small"
            label="Uninsured OK"
            variant={data.acceptsUninsuredPatients ? 'filled' : 'outlined'}
            color={data.acceptsUninsuredPatients ? 'success' : 'secondary'}
          />
          <Chip
            size="small"
            label="Keep Uninsured"
            variant={data.maintainsUninsuredPatients ? 'filled' : 'outlined'}
            color={data.maintainsUninsuredPatients ? 'success' : 'secondary'}
          />
        </Stack>
      </TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <CBOStatus status={data.data} />
      </TableCell>
    </TableRowLink>
  )
}
